import { PageContainer } from '@/components/organisms'
import { DynamicColumnTable } from '@/components/organisms'
import { useQuery } from '@tanstack/react-query'
import { getTierStores } from '@/services/ClubPlus/TierService'
import { useMemo, useState } from 'react'
import { SIZE } from '@/utils/constants/globalStyle'
import { useIntl } from 'react-intl'
import { MoveStoresModal } from '@/containers/ClubPlus/Tabs/TierTab/MoveStoresModal'
import { format, formatISO, endOfDay } from "date-fns";
import { useDebounce } from "@/hooks/useDebounce";
import './_tierTab.scss'

const INIT_FILTER = {
  lastModifiedDate: '',
  storeId: ''
}

export const TierTab = ({ tierID, tierName }) => {
  const [selectedStores, setSelectedStores] = useState([])
  const [search, setSearch] = useState(() => INIT_FILTER)
  const debouncedSearch = useDebounce(search, 1000);
  const searchParams = useMemo(() => {
    const keys = Object.keys(debouncedSearch)
    return keys.reduce((acc, currentKey) => {
      const currentValue = debouncedSearch[currentKey]
      const currentParam = currentValue !== '' ? `&${currentKey}=${currentValue}` : ''

      return acc + currentParam
    }, '')
  }, [debouncedSearch])

  const { formatMessage } = useIntl()
  const { data } = useQuery({
    queryKey: ['tier-stores', tierID, searchParams],
    queryFn: () => getTierStores(tierID, searchParams),
  })
  const [moveStoresModalOpen, setMoveStoresModalOpen] = useState(false)

  const stores = data?.stores ?? []
  const pagination = data?.pagination ?? {}

  const columns = useMemo(() => [
    {
      columnKey: 'storeId',
      name_of_db_column: 'storeId',
      header: formatMessage({ id: 'CLUB_PLUS.TIER_STORES_TABLE_STORE_ID_COLUMN' }),
      cellData: ctx => {
        const storeId = ctx?.row?.original?.storeId

        return <span className="ml-4">{storeId}</span>
      },
    },
    {
      name_of_db_column: 'lastModifiedDate',
      header: formatMessage({ id: 'CLUB_PLUS.TIER_STORES_TABLE_LAST_UPDATED_COLUMN' }),
      cellData: ctx => {
        const lastModifiedDate = ctx?.row?.original?.lastModifiedDate
        if (!lastModifiedDate) return '-'

        return format(new Date(lastModifiedDate), "yyyy-MM-dd");
      },
      filterVariant: 'date'
    }], [formatMessage])

  const toolbarActionsDisabled = selectedStores.length === 0;

  return (
    <PageContainer key={tierID}>
      <div className="stores-table">
        <DynamicColumnTable
          columns={columns}
          records={stores}
          tableIsLoading={data === undefined}
          setRowSelection={(rowsSelection) => {
            if (rowsSelection?.length > 0) {
              const newSelectedStores = rowsSelection.map(row => row?.original?.storeId)
              setSelectedStores(newSelectedStores)
            }
          }}
          filters={[]}
          toolbarActions={[
            {
              text: formatMessage({ id: 'CLUB_PLUS.TIER_STORES_ADD_STORES' }),
              variant: 'secondary',
              onClick: () => console.log(selectedStores),
              disabled: toolbarActionsDisabled
            },
            {
              text: formatMessage({ id: 'CLUB_PLUS.TIER_STORES_MOVE_STORES' }),
              variant: 'secondary',
              onClick: () => setMoveStoresModalOpen(true),
              disabled: toolbarActionsDisabled
            },
          ]}
          showFilterToolbar
          showFilterButton
          showColumnFilters
          showGlobalActions
          resetFilterButtonOptions={{
            onFiltersReset: () => {
              setSearch(INIT_FILTER)
            }
          }}
          setSearchArry={(search) => {
            let newValue;

            if (search.field === 'lastModifiedDate') {
              const searchDate = endOfDay(new Date(search.value))
              newValue = formatISO(searchDate);
            } else {
              newValue = search.value;
            }
            
            setSearch(oldSearch => ({
              ...oldSearch,
              [search.field]: newValue
            }))
          }}
          size={SIZE.LARGE}
          pageIndex={pagination?.currentPage - 1}
          totalRowsCount={pagination?.totalStores}
          pageSize={50}
          setPageIndex={(value) => {
            console.log('pagination', value)
          }}
        />
      </div>

      <MoveStoresModal
        isOpen={moveStoresModalOpen}
        closeModal={() => setMoveStoresModalOpen(false)}
        selectedStores={selectedStores}
        tierName={tierName}
        tierID={tierID}
      />
    </PageContainer>
  )
}
